import { Graphics, GRAPHICS_CURVES, LINE_CAP, LINE_JOIN } from "pixi.js"

// The glyphs the command wheel puts in its slices, one per order or verb.
//
// The wheel used to print each order's NAME inside its wedge, and a wedge is the worst shape there
// is for a line of text: wide at the rim, nothing at the hub, and on the sides of the wheel it is
// tall where the words are wide. Six orders meant two-line labels shrunk to fit, colliding at the
// seams. A glyph fits a wedge at any angle, so the wedge now carries this, and the name moves out
// to a plaque past the rim where it can sit on one line.
//
// Every glyph is drawn WHITE on a 24-unit box centred on the origin and coloured by `tint`, so one
// shape serves the resting parchment ink, the hover gold and the muted "too far" state, and the
// wheel never redraws a glyph to recolour it. Chunky round-capped strokes and solid fills only: at
// ~20px a hairline or a fine detail is noise.

export type WheelIcon =
    | "axe" | "flame" | "tent" | "hammer" | "footsteps" | "swap" | "shield" | "flag" | "retry"
    | "hand" | "sack" | "spark" | "crown" | "scroll" | "meat" | "speech" | "stairs" | "eye"
    | "boot" | "swords" | "pick" | "lens" | "sickle" | "person" | "chest" | "dot"

// The box every glyph is authored in. Callers scale a glyph by `size / WHEEL_ICON_BOX`.
export const WHEEL_ICON_BOX = 24

// Centred glyph for use outside the wheel, with the same geometry and tintable white ink.
export const createWheelIcon = (icon: WheelIcon, size: number, color: number): Graphics => {
    const glyph = new Graphics()
    drawWheelIcon(glyph, icon)
    glyph.scale.set(size / WHEEL_ICON_BOX)
    glyph.tint = color
    return glyph
}

// By command name: the role orders, then the entity interaction verbs (InteractionType), then the
// HUD's own slices and the tile verbs.
const COMMAND_ICONS: Record<string, WheelIcon> = {
    "forager:forage": "axe",
    "forager:feed_fire": "flame",
    "forager:head_back": "tent",
    "forager:repair": "hammer",
    "defender:defend": "shield",
    "defender:hold": "flag",
    "navigation:retry": "retry",
    take: "hand",
    inventory: "sack",
    burn: "flame",
    button: "spark",
    crown: "crown",
    command: "scroll",
    food: "meat",
    chat: "speech",
    help: "hand",
    descend: "stairs",
    __watch: "eye",
    "ember:kick": "boot",
    "ember:play_mode": "swords",
    dismantle: "pick",
    inspect: "lens",
    harvest: "sickle",
}

// A few verbs mean something different on a different entity, and say so in their label: "take" on
// a chest is "Open", which deserves the chest rather than a grabbing hand.
const LABEL_ICONS: Record<string, WheelIcon> = {
    Open: "chest",
}

export const wheelIconFor = (command: string, label?: string): WheelIcon => {
    if (label && LABEL_ICONS[label]) return LABEL_ICONS[label]
    if (COMMAND_ICONS[command]) return COMMAND_ICONS[command]
    if (command.endsWith(":change_role")) return "swap"
    if (command.endsWith(":follow")) return "footsteps"
    return "dot"
}

// The hub's glyph for a page. Role pages are their tool; an interaction page is given an emoji by
// its caller, which maps onto the glyph that means the same thing (undefined: no glyph for it, and
// the hub prints the emoji itself).
export const wheelHubIconFor = (page: string, emoji?: string): WheelIcon | undefined => {
    if (page === "forager") return "axe"
    if (page === "defender") return "shield"
    if (emoji === "✋") return "hand"
    if (emoji === "👤") return "person"
    return undefined
}

const stroke = (g: Graphics, width = 2.4) =>
    g.lineStyle({ width, color: 0xffffff, alpha: 1, cap: LINE_CAP.ROUND, join: LINE_JOIN.ROUND })
const solid = (g: Graphics) => g.lineStyle(0).beginFill(0xffffff, 1)

/**
 * Draw `icon` into `g` (not cleared) on the 24-unit box centred at the origin.
 *
 * pixi turns an arc or a curve into straight segments at the moment it is drawn, sized for the
 * coordinates it is drawn in -- and on a 24-unit box that is a handful of facets, which show once the
 * glyph is scaled up for a hi-dpi screen (the retry arrow came out an octagon). So the curve
 * resolution is tightened for the length of one glyph and put back.
 */
export const drawWheelIcon = (g: Graphics, icon: WheelIcon) => {
    const maxLength = GRAPHICS_CURVES.maxLength
    GRAPHICS_CURVES.maxLength = 1.5
    try {
        drawGlyph(g, icon)
    } finally {
        GRAPHICS_CURVES.maxLength = maxLength
    }
}

const drawGlyph = (g: Graphics, icon: WheelIcon) => {
    switch (icon) {
        case "axe": {
            stroke(g, 2.8).moveTo(-8.5, 11).lineTo(3.5, -6.5)
            solid(g)
                .moveTo(-0.5, -9.5)
                .lineTo(6.5, -12)
                .quadraticCurveTo(14.5, -5.5, 11, 3)
                .lineTo(3.8, -2.2)
                .closePath()
                .endFill()
            stroke(g, 2.4).moveTo(1.2, -8.2).lineTo(-2.4, -11.2)
            break
        }
        case "flame": {
            solid(g)
                .moveTo(0, 11)
                .bezierCurveTo(-7, 11, -9.5, 4.5, -6.5, -0.5)
                .bezierCurveTo(-5, -3, -3.5, -2.5, -3, -5)
                .bezierCurveTo(-1.5, -7.5, 1, -9, 0.5, -12)
                .bezierCurveTo(5, -9, 9.5, -3.5, 8.5, 3)
                .bezierCurveTo(8, 8, 4.5, 11, 0, 11)
            g.beginHole()
                .moveTo(0.5, 9.4)
                .bezierCurveTo(-3, 9.4, -4.2, 6.2, -2.8, 3.4)
                .bezierCurveTo(-1.8, 1.6, -0.4, 1, 0.2, -1.8)
                .bezierCurveTo(2.4, 0.4, 4.4, 3.2, 3.8, 6)
                .bezierCurveTo(3.4, 8.4, 2, 9.4, 0.5, 9.4)
            g.endHole().endFill()
            break
        }
        case "tent": {
            solid(g).moveTo(-11, 9.5).lineTo(0, -7.5).lineTo(11, 9.5).closePath()
            g.beginHole().moveTo(-3.6, 9.5).lineTo(0, 2.6).lineTo(3.6, 9.5).closePath().endHole().endFill()
            stroke(g, 2).moveTo(0, -7).lineTo(-3, -11.5).moveTo(0, -7).lineTo(3, -11.5)
            break
        }
        case "hammer": {
            stroke(g, 2.8).moveTo(-8.5, 10).lineTo(2.5, -1)
            solid(g).moveTo(1.4, -11.8).lineTo(11.8, -1.4).lineTo(7.6, 2.8).lineTo(-2.8, -7.6).closePath().endFill()
            solid(g).moveTo(-2.8, -7.6).lineTo(1.4, -11.8).lineTo(-1.6, -12.4).lineTo(-4.6, -9.4).closePath().endFill()
            break
        }
        case "footsteps": {
            solid(g)
            g.drawEllipse(-5, 1.6, 4, 5.4)
            g.drawEllipse(-5, 9.8, 3, 2.2)
            g.drawEllipse(5, -7.8, 4, 5.4)
            g.drawEllipse(5, 0.4, 3, 2.2)
            g.endFill()
            break
        }
        case "swap": {
            stroke(g, 2.6)
            g.moveTo(-9.5, -4.5).lineTo(8.5, -4.5).moveTo(4.2, -9).lineTo(8.8, -4.5).lineTo(4.2, 0)
            g.moveTo(9.5, 5).lineTo(-8.5, 5).moveTo(-4.2, 0.5).lineTo(-8.8, 5).lineTo(-4.2, 9.5)
            break
        }
        case "shield": {
            solid(g)
                .moveTo(0, -11.5)
                .lineTo(9.5, -8.5)
                .lineTo(9.5, -1)
                .bezierCurveTo(9.5, 5.5, 5, 9.5, 0, 12)
                .bezierCurveTo(-5, 9.5, -9.5, 5.5, -9.5, -1)
                .lineTo(-9.5, -8.5)
                .closePath()
            g.beginHole()
                .moveTo(0, -8.6)
                .lineTo(6.8, -6.5)
                .lineTo(6.8, -1)
                .bezierCurveTo(6.8, 3.8, 3.6, 6.8, 0, 8.9)
                .bezierCurveTo(-3.6, 6.8, -6.8, 3.8, -6.8, -1)
                .lineTo(-6.8, -6.5)
                .closePath()
            g.endHole().endFill()
            stroke(g, 2.4).moveTo(0, -6).lineTo(0, 6).moveTo(-4.4, -1.6).lineTo(4.4, -1.6)
            break
        }
        case "flag": {
            stroke(g, 2.4).moveTo(-6, -11).lineTo(-6, 10.5)
            solid(g)
                .moveTo(-5, -10.8)
                .bezierCurveTo(-1, -12.4, 3, -8.6, 9.5, -9.6)
                .lineTo(8, -5.4)
                .lineTo(9.5, -1.2)
                .bezierCurveTo(3, -0.2, -1, -4, -5, -2.4)
                .closePath()
                .endFill()
            g.lineStyle(0).beginFill(0xffffff, 0.55).drawEllipse(-6, 10.8, 5, 1.6).endFill()
            break
        }
        case "retry": {
            const r = 8
            const from = -Math.PI / 2 + 0.55
            const to = from + Math.PI * 2 - 1.1
            stroke(g, 2.6).moveTo(Math.cos(from) * r, 1 + Math.sin(from) * r)
            g.arc(0, 1, r, from, to)
            const ex = Math.cos(to) * r
            const ey = 1 + Math.sin(to) * r
            solid(g).moveTo(ex + 5, ey + 0.4).lineTo(ex - 1.4, ey + 4.6).lineTo(ex - 1.2, ey - 4.4).closePath().endFill()
            break
        }
        case "hand": {
            solid(g).drawRoundedRect(-6.5, -1.5, 12.5, 11, 4).endFill()
            stroke(g, 3)
            g.moveTo(-4.5, 0).lineTo(-4.5, -6.8)
            g.moveTo(-1.4, 0).lineTo(-1.4, -9.4)
            g.moveTo(1.7, 0).lineTo(1.7, -8.8)
            g.moveTo(4.6, 1).lineTo(4.6, -5.6)
            g.moveTo(-5, 4.5).lineTo(-9.6, -0.6)
            break
        }
        case "sack": {
            solid(g)
                .moveTo(-3.2, -5.8)
                .bezierCurveTo(-11.5, -1.5, -11, 10.5, 0, 10.5)
                .bezierCurveTo(11, 10.5, 11.5, -1.5, 3.2, -5.8)
                .closePath()
                .endFill()
            solid(g).moveTo(-3, -7.4).lineTo(-6.5, -11.2).lineTo(0, -9.4).lineTo(6.5, -11.2).lineTo(3, -7.4).closePath().endFill()
            stroke(g, 1.6).moveTo(-4.2, -6.6).lineTo(4.2, -6.6)
            break
        }
        case "spark": {
            solid(g)
                .moveTo(0, -11.5)
                .quadraticCurveTo(1.6, -1.6, 11.5, 0)
                .quadraticCurveTo(1.6, 1.6, 0, 11.5)
                .quadraticCurveTo(-1.6, 1.6, -11.5, 0)
                .quadraticCurveTo(-1.6, -1.6, 0, -11.5)
                .endFill()
            break
        }
        case "crown": {
            solid(g)
                .moveTo(-10, 5.5)
                .lineTo(-11, -6)
                .lineTo(-5, -0.5)
                .lineTo(0, -9)
                .lineTo(5, -0.5)
                .lineTo(11, -6)
                .lineTo(10, 5.5)
                .closePath()
            g.drawRect(-10, 7.5, 20, 3.2)
            g.drawCircle(-11, -7, 1.9)
            g.drawCircle(0, -10, 1.9)
            g.drawCircle(11, -7, 1.9)
            g.endFill()
            break
        }
        case "scroll": {
            solid(g).drawRoundedRect(-7.5, -10, 15, 20, 2)
            g.beginHole()
            g.drawRect(-4.5, -5.5, 9, 1.7)
            g.drawRect(-4.5, -1.5, 9, 1.7)
            g.drawRect(-4.5, 2.5, 6, 1.7)
            g.endHole().endFill()
            solid(g).drawRoundedRect(-10, -11.5, 20, 3.4, 1.7).drawRoundedRect(-10, 8.1, 20, 3.4, 1.7).endFill()
            break
        }
        case "meat": {
            solid(g).drawCircle(-2.8, -2.8, 7.8).endFill()
            stroke(g, 3.4).moveTo(1.5, 1.5).lineTo(7.5, 7.5)
            solid(g).drawCircle(7.6, 10.2, 2.4).drawCircle(10.2, 7.6, 2.4).endFill()
            break
        }
        case "speech": {
            solid(g).drawRoundedRect(-11, -10, 22, 15.5, 5)
            g.beginHole()
            g.drawCircle(-5, -2.3, 1.7)
            g.drawCircle(0, -2.3, 1.7)
            g.drawCircle(5, -2.3, 1.7)
            g.endHole()
            g.moveTo(-6, 4).lineTo(-8, 10.5).lineTo(0.5, 4.5).closePath().endFill()
            break
        }
        case "stairs": {
            solid(g)
                .moveTo(-11, -3.5)
                .lineTo(-6, -3.5)
                .lineTo(-6, 1.5)
                .lineTo(-1, 1.5)
                .lineTo(-1, 6.5)
                .lineTo(4, 6.5)
                .lineTo(4, 11)
                .lineTo(-11, 11)
                .closePath()
                .endFill()
            stroke(g, 2.4).moveTo(8.5, -11).lineTo(8.5, -0.5).moveTo(5, -4).lineTo(8.5, -0.5).lineTo(12, -4)
            break
        }
        case "eye": {
            stroke(g, 2.2).moveTo(-11.5, 0).quadraticCurveTo(0, -12, 11.5, 0).quadraticCurveTo(0, 12, -11.5, 0)
            solid(g).drawCircle(0, 0, 4.8)
            g.beginHole().drawCircle(0, 0, 1.9).endHole().endFill()
            break
        }
        case "boot": {
            solid(g)
                .moveTo(-7, -11)
                .lineTo(1.5, -11)
                .lineTo(1.5, 0.5)
                .bezierCurveTo(6.5, 1, 10.5, 3, 10.5, 7)
                .lineTo(10.5, 8.5)
                .lineTo(-7, 8.5)
                .closePath()
            g.drawRect(-7, 9.8, 17.5, 2)
            g.endFill()
            break
        }
        case "swords": {
            stroke(g, 2.8)
            g.moveTo(-10, -10).lineTo(5.5, 5.5).moveTo(2, 9).lineTo(9, 2).moveTo(6.5, 6.5).lineTo(10.5, 10.5)
            g.moveTo(10, -10).lineTo(-5.5, 5.5).moveTo(-2, 9).lineTo(-9, 2).moveTo(-6.5, 6.5).lineTo(-10.5, 10.5)
            break
        }
        case "pick": {
            stroke(g, 2.8).moveTo(-8.5, 10.5).lineTo(3.5, -1.5)
            // A tapered crescent: two curves between the same two points, the outer one fuller.
            solid(g).moveTo(-7.5, -8.5).quadraticCurveTo(7.5, -15, 11, 3.5).quadraticCurveTo(5, -7, -7.5, -8.5).endFill()
            break
        }
        case "lens": {
            stroke(g, 2.6).drawCircle(-2.5, -2.5, 6.8)
            stroke(g, 3.6).moveTo(2.6, 2.6).lineTo(9.5, 9.5)
            break
        }
        case "sickle": {
            stroke(g, 2.8).moveTo(-9.5, 11).lineTo(-4.5, 5.5)
            solid(g)
                .moveTo(-5.5, 5.5)
                .bezierCurveTo(-12, -5, 0, -15.5, 10.5, -4.5)
                .bezierCurveTo(3, -9.5, -6.5, -6.5, -2.5, 3.5)
                .closePath()
                .endFill()
            break
        }
        case "person": {
            solid(g).drawCircle(0, -5, 5.2)
            g.moveTo(-10.5, 11.5)
                .bezierCurveTo(-10.5, 3.5, -5.5, 1.8, 0, 1.8)
                .bezierCurveTo(5.5, 1.8, 10.5, 3.5, 10.5, 11.5)
                .closePath()
                .endFill()
            break
        }
        case "chest": {
            solid(g).moveTo(-10.5, -2.5).lineTo(-10.5, -5.5).quadraticCurveTo(0, -12.5, 10.5, -5.5).lineTo(10.5, -2.5).closePath()
            g.drawRect(-10.5, -0.8, 21, 10.8)
            g.beginHole().drawRect(-1.4, 1.2, 2.8, 4).endHole()
            g.endFill()
            break
        }
        case "dot":
        default: {
            solid(g).moveTo(0, -7).lineTo(7, 0).lineTo(0, 7).lineTo(-7, 0).closePath().endFill()
            break
        }
    }
    g.lineStyle(0)
}
